/**
 * SD-3875 — Mesure la largeur réelle de la scrollbar verticale du navigateur
 * et l'expose comme variable CSS `--scrollbar-width` sur `:root`.
 *
 * Le composable mesure à la fois :
 *  - la scrollbar par défaut du navigateur (variable CSS `--scrollbar-width-native`)
 *  - la scrollbar custom appliquée via la classe `scrollbar-thin` (variable
 *    CSS `--scrollbar-width`) — c'est cette dernière qui est utilisée pour
 *    aligner les bords droits dans la sidebar GGVie.
 *
 * À appeler une seule fois au boot de l'app (App.vue ou main.ts).
 * Réagit aussi au resize pour les cas où la scrollbar change (zoom, etc.).
 */
import { onBeforeUnmount, onMounted } from 'vue'

function measure(className?: string): number {
  const outer = document.createElement('div')
  if (className) outer.className = className
  // `scrollbar-gutter: stable` réserve TOUJOURS l'espace de la scrollbar
  // (que la scrollbar soit overlay ou stable). Du coup
  // `offsetWidth - clientWidth` donne la largeur de gutter quel que soit
  // le navigateur / OS / headless mode — donc consistant avec
  // `.conv-list` qui utilise la même règle CSS.
  outer.style.cssText =
    'position:absolute;top:-9999px;left:-9999px;visibility:hidden;width:100px;height:100px;overflow:auto;scrollbar-gutter:stable;'
  const inner = document.createElement('div')
  inner.style.cssText = 'width:100%;height:200px;'
  outer.appendChild(inner)
  document.body.appendChild(outer)
  const width = outer.offsetWidth - outer.clientWidth
  outer.remove()
  return width
}

function applyMeasurements(): void {
  const native = measure()
  const custom = measure('gg-sidebar-scrollbar-probe')
  const root = document.documentElement
  root.style.setProperty('--scrollbar-width-native', `${native}px`)
  root.style.setProperty('--scrollbar-width', `${custom}px`)
}

export function useScrollbarWidth(): void {
  const handler = (): void => applyMeasurements()
  onMounted(() => {
    applyMeasurements()
    window.addEventListener('resize', handler)
  })
  onBeforeUnmount(() => {
    window.removeEventListener('resize', handler)
  })
}
